import { Object3D, PlaneGeometry, MeshBasicMaterial, Mesh, TextureLoader, SRGBColorSpace } from "three";
import * as htmlToImage from "html-to-image";

import { config } from "./config";
import { TO_RAD } from "~/lib/utils.js";

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator?.userAgent);
const BATCH_SIZE = isMobile ? 1 : 1; // Process only a few at a time
let processingQueue = [];
let isProcessing = false;
let geometryCache = {}; // Shared geometry cache

export default class MilestoneEvent extends Object3D {
    constructor(event, milestoneIndex, eventIndex) {
        super();
        this.event = event;
        const id = `slt-js-milestone-event-${milestoneIndex}-${eventIndex}`;

        const ele = document.getElementById(id);
        if (!ele) return;

        const eleW = ele.offsetWidth;
        const eleH = ele.offsetHeight;
        this.eleW = eleW * config.pxToWorld;
        this.eleH = eleH * config.pxToWorld;

        // Use shared geometry
        const geometryKey = `${this.eleW.toFixed(2)}-${this.eleH.toFixed(2)}`;
        const geometry =
            geometryCache[geometryKey] ||
            (geometryCache[geometryKey] = new PlaneGeometry(
                this.eleW,
                this.eleH,
            ));
        // Create a simple placeholder immediately
        const tempMaterial = new MeshBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 0.5,
        });

        const mesh = new Mesh(geometry, tempMaterial);
        mesh.rotation.y = config.camera.rotateY * TO_RAD;
        mesh.position.set(-10, -10, 0);
        this.add(mesh);

        // Track if this instance has been processed
        this.processed = false;

        // Add to processing queue instead of immediate processing
        processingQueue.push({
            element: ele,
            mesh: mesh,
            instance: this,
        });

        // Start processing if not already running
        if (!isProcessing) {
            MilestoneEvent.processNextBatch();
        }
    }
    // Static method to process queue in batches
    static processNextBatch() {
        if (processingQueue.length === 0) {
            isProcessing = false;
            return;
        }

        isProcessing = true;
        const batch = processingQueue.splice(0, BATCH_SIZE);

        // Process batch
        Promise.all(
            batch.map((item) => {
                const { element, mesh, instance } = item;
                // Skip if element is no longer in DOM
                if (!element || !document.body.contains(element))
                    return Promise.resolve();

                return htmlToImage
                    .toBlob(element, { skipFonts: true })
                    .then((blob) => {
                        if (!instance.parent) return; // Skip if removed from scene
                        // Create blob URL from the blob
                        const blobUrl = URL.createObjectURL(blob);
                        // Create texture loader
                        const loader = new TextureLoader();
                        // Load the blob URL as a texture
                        loader.load(blobUrl, (texture) => {
                            // Set texture properties
                            texture.colorSpace = SRGBColorSpace;
                            texture.generateMipmaps = false;

                            // Update material
                            mesh.material.dispose();
                            mesh.material = new MeshBasicMaterial({
                                map: texture,
                                transparent: true,
                                depthWrite: false,
                            });

                            // Clean up blob URL
                            URL.revokeObjectURL(blobUrl);

                            instance.processed = true;
                        });
                    })
                    .catch((err) => {
                        // console.error("oops, something went wrong!", err);
                    });
            }),
        ).then(() => {
            // Schedule next batch with delay
            setTimeout(
                () => {
                    MilestoneEvent.processNextBatch();
                },
                isMobile ? 300 : 100,
            ); // Longer delay on mobile
        });
    }
    destroy() {
        // Dispose of all child meshes and their resources
        this.traverse((object) => {
            if (object.geometry) {
                object.geometry.dispose();
            }

            if (object.material) {
                if (object.material.map) {
                    object.material.map.dispose();
                }
                object.material.dispose();
            }
        });

        // Clear all children
        while (this.children.length > 0) {
            this.remove(this.children[0]);
        }
    }
}
